import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import auth_service from "../../../src/services/auth_service";
import api_service from "../../../src/services/api_services";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { motion } from "framer-motion";
import { FaUser, FaEnvelope, FaPhone, FaGift } from "react-icons/fa";
import SkeletonLoading from "../../components/SkeletonLoading";

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // ============= Initial State Start here =============
  const [clientName, setClientName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [referralCode, setReferralCode] = useState(""); // New state for referral code

  // ============= Initial State End here ===============
  // ============= Error Msg Start here =================
  const [errClientName, setErrClientName] = useState("");
  const [errEmail, setErrEmail] = useState("");
  const [errPhone, setErrPhone] = useState("");
  // ============= Error Msg End here ===================
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false); // State to manage loading status

  // Auto-fill referral code from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const referralCodeFromUrl = urlParams.get('referralCode');
    
    if (referralCodeFromUrl) {
      setReferralCode(referralCodeFromUrl);
    }
  }, [location.search]);

  // ============= Event Handler Start here =============
  const handleName = (e) => {
    setClientName(e.target.value);
    setErrClientName("");
  };
  const handleEmail = (e) => {
    setEmail(e.target.value);
    setErrEmail("");
  };

  // Phone number handler - only accepts numbers and limits length to 10
  const handlePhone = (e) => {
    const value = e.target.value;
    // Only allow digits and limit length to 10
    if (/^\d*$/.test(value) && value.length <= 10) {
      setPhone(value);
    }
    setErrPhone("");
  };

  const handleReferralCode = (e) => {
    setReferralCode(e.target.value); // Handle referral code
  };
  // ============= Event Handler End here ===============

  // ================= Email Validation start here =============
  const EmailValidation = (email) => {
    return String(email)
      .toLowerCase()
      .match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i);
  };
  // ================= Email Validation End here ===============

  const handleSignUp = async (e) => {
    e.preventDefault();

    // Validate inputs
    if (!clientName) {
      setErrClientName("Enter your name");
    }
    if (!email) {
      setErrEmail("Enter your email");
    } else {
      if (!EmailValidation(email)) {
        setErrEmail("Enter a valid email");
      }
    }
    if (!phone) {
      setErrPhone("Enter your phone number");
    } else if (phone.length !== 10) {
      setErrPhone("Phone number must be 10 digits");
    }

    // If no validation errors, proceed with the API call
    if (
      clientName &&
      email &&
      EmailValidation(email) &&
      phone &&
      phone.length === 10
    ) {
      setLoading(true); // Set loading to true while waiting for the API call

      try {
        const user = {
          name: clientName,
          email,
          mobile: phone, // You can add phone as per your API requirement
          referred_by: referralCode || null, // Include referral code if provided
        };

        // Call the API method to create a new user
        const response = await auth_service.addNewUser(user);

        // If the API call is successful, display success message
        if (response.success === true) {
          toast.success("Account created successfully", {
            duration: 3000,
            position: "top-center",
            style: {
              background: "#333",
              color: "#fff",
            },
          });
          // Clear form fields
          setClientName("");
          setEmail("");
          setPhone("");
          setReferralCode(""); // Clear referral code

          // Delay navigation to give time for the toast to show
          setTimeout(() => {
            navigate("/signin");
          }, 3000); // Delay of 2 seconds (2000 milliseconds)
        }
      } catch (error) {
        console.error("Error during sign-up:", error);
        setErrEmail(
          "An error occurred while creating the account. Please try again."
        );
      } finally {
        setLoading(false); // Set loading to false after the API call
      }
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  return (
    <div
      className="w-full min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background: `linear-gradient(to right, rgb(31, 80, 154), rgb(0, 0, 102))`,
      }}
    >
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="bg-white shadow-2xl rounded-2xl w-full max-w-lg overflow-hidden"
      >
        {/* Header */}
        <div 
          className="p-8 text-white text-center"
          style={{
            background: `linear-gradient(to right, rgb(31, 80, 154), rgb(0, 0, 102))`
          }}
        >
          <div className="w-12 h-1 bg-gradient-to-r from-yellow-400 to-amber-500 mx-auto mb-6 rounded-full"></div>
          
          <motion.h1 
            variants={itemVariants}
            className="text-3xl font-bold mb-2"
          >
            Create Account
          </motion.h1>
          <motion.p 
            variants={itemVariants}
            className="text-blue-100 opacity-90"
          >
            Join our community today
          </motion.p>
        </div>

        <div className="p-8">
          <form className="space-y-6" onSubmit={handleSignUp}>
            {/* Full Name */}
            <motion.div variants={itemVariants} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <div className="relative">
                
                <input
                  onChange={handleName}
                  value={clientName}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
                  type="text"
                  placeholder="Enter your full name"
                />
              </div>
              {errClientName && (
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-600 text-sm"
                >
                  {errClientName}
                </motion.p>
              )}
            </motion.div>

            {/* Email */}
            <motion.div variants={itemVariants} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="relative">
               
                <input
                  onChange={handleEmail}
                  value={email}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
                  type="email"
                  placeholder="Enter your email"
                />
              </div>
              {errEmail && (
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-600 text-sm"
                >
                  {errEmail}
                </motion.p>
              )}
            </motion.div>

            {/* Phone Number */}
            <motion.div variants={itemVariants} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <div className="relative">
               
                <input
                  onChange={handlePhone}
                  value={phone}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
                  type="tel"
                  placeholder="10-digit phone number"
                />
              </div>
              {errPhone && (
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-600 text-sm"
                >
                  {errPhone}
                </motion.p>
              )}
<p className="text-sm text-amber-600">
                  Enter whatsapp number for verification
                </p>            </motion.div>

            {/* Referral Code */}
            <motion.div variants={itemVariants} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Referral Code (Optional)
              </label>
              <div className="relative">
                
                <input
                  onChange={handleReferralCode}
                  value={referralCode}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
                  type="text"
                  placeholder="Enter referral code if you have one"
                />
              </div>
              <p className="text-sm text-amber-600">💡 Get 50 bonus points with a valid referral code!</p>
            </motion.div>

            {/* Create Account Button */}
            <motion.button
              variants={itemVariants}
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg text-white font-medium transition-all duration-200 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50 mt-6"
              style={{
                background: `linear-gradient(to right, rgb(31, 80, 154), rgb(0, 0, 102))`,
              }}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Creating Account...
                </div>
              ) : (
                "Create Account"
              )}
            </motion.button>

            {/* Sign In Link */}
            <motion.p variants={itemVariants} className="text-sm text-center mt-6 text-gray-600">
              Already have an account?{" "}
              <Link 
                to="/signin" 
                className="font-medium text-amber-600 hover:text-amber-500 transition-colors duration-200"
              >
                Sign in
              </Link>
            </motion.p>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default SignUp;